import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { doc, onSnapshot } from "firebase/firestore";
import { Link as RouterLink } from "react-router-dom";
import { db } from "../../data/firebase";
import {
  errorMessage,
  fmtDay,
  fmtRange,
  respondToCoverGroup,
  STATUS_LABELS,
} from "../../utils/coversApi";

const statusColor = (s) => ({ open: "warning", taken: "success" }[s] || "default");

// Card tint by status: yellow while a request is open, green once taken.
// Light mode uses a soft fill; dark mode keeps the dark background and uses
// a coloured border with only a faint tint so text stays readable.
const CARD_TONES = {
  light: {
    open: { bgcolor: "#FFF8E1", border: "1px solid #F9C74F", color: "#3E2F00" },
    taken: { bgcolor: "#E8F5E9", border: "1px solid #66BB6A", color: "#0F3D14" },
    cancelled: { bgcolor: "#F3F3F3", border: "1px solid #CFCFCF", color: "#444", opacity: 0.85 },
    expired: { bgcolor: "#F3F3F3", border: "1px solid #CFCFCF", color: "#444", opacity: 0.85 },
  },
  dark: {
    open: { bgcolor: "rgba(249,199,79,0.08)", border: "2px solid #B98900" },
    taken: { bgcolor: "rgba(102,187,106,0.08)", border: "2px solid #2E7D32" },
    cancelled: { bgcolor: "rgba(255,255,255,0.03)", border: "1px solid #555", opacity: 0.8 },
    expired: { bgcolor: "rgba(255,255,255,0.03)", border: "1px solid #555", opacity: 0.8 },
  },
};

/**
 * Live card rendered inside a chat message for widget.kind === "coverRequest".
 * Subscribes to the cover group so every copy shows the same state.
 */
const CoverRequestCard = ({ groupId, currentUserUid }) => {
  const theme = useTheme();
  const [group, setGroup] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!groupId) return undefined;
    const unsub = onSnapshot(
      doc(db, "coverGroups", groupId),
      (snap) => setGroup(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      () => setGroup(null)
    );
    return () => unsub();
  }, [groupId]);

  const respond = async (response) => {
    setBusy(true);
    setError("");
    try {
      await respondToCoverGroup({ groupId, response });
      setConfirmOpen(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (group === undefined) {
    return (
      <Typography variant="caption" color="text.secondary">
        Loading cover request…
      </Typography>
    );
  }
  if (group === null) {
    return (
      <Typography variant="caption" color="text.secondary">
        This cover request is no longer available.
      </Typography>
    );
  }

  const sessions = [...(group.sessions || [])].sort((a, b) =>
    a.startDateTime.localeCompare(b.startDateTime)
  );
  const mine = group.invited?.[currentUserUid];
  const isInvitee = !!mine;
  const isOpen = group.status === "open";
  const iAccepted = group.acceptedBy === currentUserUid;
  const invitedList = Object.values(group.invited || {});
  const accepted = invitedList.filter((i) => i.response === "accepted").length;
  const declined = invitedList.filter((i) => i.response === "declined").length;
  const pending = invitedList.length - accepted - declined;

  let statusLine = null;
  if (group.status === "taken") {
    statusLine = iAccepted
      ? "You are covering these sessions. Wise Minds Admin has been updated."
      : `Taken by ${group.acceptedByName}.`;
  } else if (group.status === "cancelled") {
    statusLine = `Withdrawn by ${group.cancelledByName || "the senior tutor"}.`;
  } else if (group.status === "expired") {
    statusLine = "Expired: the first session has started.";
  } else if (isInvitee && mine.response === "declined") {
    statusLine = "You declined.";
  }

  return (
    <Box
      data-testid="cover-request-card"
      data-status={group.status}
      sx={{
        mt: 1,
        p: 1.5,
        borderRadius: 2,
        minWidth: 260,
        ...CARD_TONES[theme.palette.mode === "dark" ? "dark" : "light"][group.status],
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} mb={1}>
        <Typography fontWeight="bold">Cover for {group.originalTutorName || "a tutor"}</Typography>
        <Chip size="small" color={statusColor(group.status)} label={STATUS_LABELS[group.status] || group.status} />
      </Stack>

      <Stack gap={0.5} mb={1}>
        {sessions.map((s) => (
          <Box key={s.lessonId}>
            <Typography variant="body2">
              <b>{fmtDay(s.startDateTime)}</b> · {fmtRange(s.startDateTime, s.endDateTime)} ·{" "}
              {s.subjectGroupName || "Lesson"}
              {(group.originalTutorIds || []).length > 1 && s.originalTutorName && ` · for ${s.originalTutorName}`}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              {(s.studentNames || []).join(", ") || "No students"}
              {s.locationName && ` · ${s.locationName}`}
            </Typography>
          </Box>
        ))}
      </Stack>

      {statusLine && (
        <Typography variant="body2" fontWeight="bold" mb={isOpen ? 1 : 0}>
          {statusLine}
        </Typography>
      )}

      {isOpen && isInvitee && mine.response !== "declined" && (
        <Stack direction="row" gap={1}>
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<CheckIcon />}
            disabled={busy}
            onClick={() => setConfirmOpen(true)}
          >
            Accept
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<CloseIcon />}
            disabled={busy}
            onClick={() => respond("decline")}
          >
            Decline
          </Button>
        </Stack>
      )}
      {isOpen && isInvitee && mine.response === "declined" && (
        <Button size="small" disabled={busy} onClick={() => respond("undo")}>
          Undo decline
        </Button>
      )}

      {!isInvitee && isOpen && invitedList.length > 0 && declined === invitedList.length && (
        <Typography variant="body2" fontWeight="bold" color="error.main" display="block">
          Everyone has declined. Add more tutors or cancel the request.
        </Typography>
      )}
      {!isInvitee && (
        <Typography variant="caption" sx={{ opacity: 0.75 }} display="block">
          {invitedList.length} invited · {declined} declined · {pending} waiting ·{" "}
          <RouterLink to="/covers" style={{ color: "inherit" }}>
            Manage
          </RouterLink>
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1, py: 0 }}>
          {error}
        </Alert>
      )}

      <Dialog open={confirmOpen} onClose={busy ? undefined : () => setConfirmOpen(false)}>
        <DialogTitle>Accept this cover?</DialogTitle>
        <DialogContent>
          <Typography mb={1.5}>
            You'll be covering {group.originalTutorName ? `for ${group.originalTutorName}` : ""}:
          </Typography>
          <Stack gap={1} mb={1.5}>
            {sessions.map((s) => (
              <Box key={s.lessonId} sx={{ pl: 1.5, borderLeft: "3px solid", borderColor: "success.main" }}>
                <Typography variant="body2" fontWeight="bold">
                  {fmtDay(s.startDateTime)} · {fmtRange(s.startDateTime, s.endDateTime)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {s.subjectGroupName || "Lesson"}
                  {s.locationName && ` · ${s.locationName}`}
                  {(s.studentNames || []).length > 0 && ` · ${s.studentNames.join(", ")}`}
                </Typography>
              </Box>
            ))}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Wise Minds Admin will be updated straight away: {sessions.length === 1 ? "this session" : "these sessions"}{" "}
            will move to your calendar and the other tutors will see the request is taken.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>
            Back
          </Button>
          <Button variant="contained" color="success" onClick={() => respond("accept")} disabled={busy}>
            {busy ? "Accepting…" : "Accept cover"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CoverRequestCard;
